import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {IntelligenceData} from '../../../models/intelligence-data.interface';
import {INTELLIGENCE_BAR_COLOR} from '../../../../constants/charts';
import {MessageService} from '../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../core/services/auth.service';

@Component({
  selector: 'app-members-adhered-bar-chart',
  templateUrl: './members-adhered-bar-chart.component.html',
  styleUrls: ['./members-adhered-bar-chart.component.scss']
})
export class MembersAdheredBarChartComponent implements OnInit, OnChanges {

  textBelow!: string;
  chart!: Chart;
  chartData!: SeriesOptionsType[];
  countryCodes!: string[];
  textBelowCode = 'intelligence.WeeeSystem.MembersAdhered.textBelow';
  isAdmin!: boolean;
  userSystem!: string;

  @Input()
  data!: IntelligenceData[];
  @Input()
  displayUnits!: string;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.userSystem = this.authService.getSystem();
  }

  ngOnInit(): void {
    this.messageService.getDialogInfo(this.textBelowCode).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    if (this.data) {
      // highcharts
      this.chartData = this.convertToChartData(this.data);
      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'column',
          options3d: {
            enabled: true,
            alpha: 7,
            beta: 0,
            depth: 35,
            viewDistance: 15
          }
        },
        colors: INTELLIGENCE_BAR_COLOR,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        exporting: {
          enabled: true
        },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: true,
              allowOverlap: true,
              format: `{point.custom.systemSplited}`
            },
            groupPadding: 0,
          }
        },
        tooltip: {
          formatter(): string[] | undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              if (pointOptions.custom?.absolute === 0) {
                return [pointOptions.custom?.name];
              }
              return ['<div class="center"><b>' + pointOptions.custom?.name + '</b></div><br><br>',
                '- Value: ' + pointOptions.custom?.answer + '<br>',
                '- Total respondents: ' + pointOptions.custom?.totalResponses + '<br>',
                pointOptions.custom?.isAdmin ? ('- Response by: ' + pointOptions.custom?.weeeSystemDescription + '<br>') : '',
                '- Total sum of members: ' + pointOptions.custom?.totalSum + '<br>',
                '- Average: ' + pointOptions.custom?.average.toFixed(2) + '<br>',
              ];
            }
            // If not null, use the default formatter
            // return tooltip.defaultFormatter.call(this, tooltip);
            return undefined;
          }
        },
        xAxis: {
          labels: {
            enabled: false
          },
        },
        series: this.chartData
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(['/admin/chart-labels'], {state: {code: this.textBelowCode}}).then(
      () => {
        // do nothing
      }
    );
  }


  private convertToChartData(data: IntelligenceData[]): SeriesOptionsType[] {
    this.chart?.ref?.hideData();
    const chartData = [] as SeriesOptionsType[];

    if (data.length) {
      const average = data.map(item => Number(item.answer)).reduce((a, b) => a + b) / data.length;

      chartData.push({
        name: this.data[0].year.toString(),
        type: 'column',
        data: data.map((item, index) => {
          const systemSplited = item.countryName.split(' ');
          let system = '';
          systemSplited.forEach((value: string) => {
            system = system + (value + '<br/>');
          });
          return {
            y: Number(item.answer),
            name: (this.isAdmin || this.userSystem === item.countryName) ? system : 'System' + (index + 1),
            custom: {
              answer: item.answer,
              totalResponses: data.length,
              totalSum: item.totalSum,
              average,
              weeeSystemDescription: (this.isAdmin || this.userSystem === item.countryName) ? system : 'System' + (index + 1),
              systemSplited: (this.isAdmin || this.userSystem === item.countryName) ? system : 'System' + (index + 1),
              isAdmin: this.isAdmin,
              name: (this.isAdmin || this.userSystem === item.countryName) ? system : 'System' + (index + 1)
            }
          };
        })

      });

      chartData.push({
        name: 'Average (' + average.toFixed(2) + ')',
        type: 'line',
        marker: {
          enabled: false
        },
        pointStart: 0,
        color: '#08417f',
        enableMouseTracking: false,
        data: data.map(() => Number(average.toFixed(2)))
      });
    }

    return chartData;

  }

}
